/* import __COLOCATED_TEMPLATE__ from './change-ticket-type-panel.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { TicketTypeResource } from 'embercom/components/inbox2/ticket-type-resource';
import { useResource } from 'ember-resources';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type Snackbar from 'embercom/services/snackbar';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxApi from 'embercom/services/inbox-api';
import type TicketAttributeSummary from 'embercom/objects/inbox/ticket-attribute-summary';
import type InboxState from 'embercom/services/inbox-state';
import type TicketStateService from 'embercom/services/ticket-state-service';
import { action } from '@ember/object';
import type LinkedTicketsResource from './linked-tickets-resource';
import { ResponseError } from 'embercom/lib/inbox/requests';

export interface ChangeTicketTypePanelArgs {
  conversation: Conversation;
  newTicketTypeId?: number;
  hidePanel: () => void;
  linkedTicketsResource: LinkedTicketsResource;
  onSuccess?: () => void;
}

interface Signature {
  Args: ChangeTicketTypePanelArgs;
}

export default class ChangeTicketTypePanel extends Component<Signature> {
  @service declare snackbar: Snackbar;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;
  @service declare ticketStateService: TicketStateService;

  @tracked selectedTicketCustomStateId: number | undefined;

  readonly ticketTypeResource = useResource(this, TicketTypeResource, () => ({
    ticketTypeId: this.args.newTicketTypeId,
    conversation: this.args.conversation,
    matchAttributesByNameAndType: true,
  }));

  get shouldShowAttributes() {
    let hasSelectedType = !!this.ticketTypeResource.selectedTicketType;
    let hasAttributes = this.ticketTypeResource.ticketHasAttributes;
    let isLoading = this.ticketTypeResource.isLoading;
    return hasSelectedType && hasAttributes && !isLoading;
  }

  get preselectedStatusId(): number | undefined {
    let type = this.ticketTypeResource.selectedTicketType;
    let conversation = this.args.conversation;
    let customStateId = conversation.ticketCustomStateId;
    if (!type || customStateId == null) {
      return undefined;
    }
    return this.ticketStateService.ticketStateBelongsToTicketType(type, customStateId)
      ? customStateId
      : undefined;
  }

  get effectiveSelectedStatusId(): number | undefined {
    return this.selectedTicketCustomStateId ?? this.preselectedStatusId;
  }

  get statusRequiredAndEmpty(): boolean {
    return this.effectiveSelectedStatusId == null;
  }

  get statusSelectHasError(): boolean {
    return this.statusRequiredAndEmpty && (taskFor(this.changeTicketType).performCount ?? 0) > 0;
  }

  @action
  onStatusSelect(customStateId: number) {
    this.selectedTicketCustomStateId = customStateId;
  }

  @task({ drop: true })
  *changeTicketType(): any {
    if (this.args.newTicketTypeId === undefined) {
      return;
    }

    if (this.statusRequiredAndEmpty) {
      this.snackbar.notifyError(
        this.intl.t('inbox.change-ticket-type-panel.errors.status-required'),
      );
      return;
    }

    if (this.ticketTypeResource.missingRequiredAttributes.length > 0) {
      let errorMessage = this.intl.t('inbox.new-conversation.errors.invalid-ticket-attributes', {
        required_attributes: this.ticketTypeResource.missingRequiredAttributes
          .map((attr) => attr.descriptor.name)
          .join(', '),
      });
      this.snackbar.notifyError(errorMessage);
      return;
    }

    if (this.ticketTypeResource.attributesWithInvalidTypes.length > 0) {
      let errorMessage = this.intl.t(
        'inbox.new-conversation.errors.invalid-ticket-attributes-types',
        {
          invalid_attributes: this.ticketTypeResource.attributesWithInvalidTypes
            .map((attr) => attr.descriptor.name)
            .join(', '),
        },
      );
      this.snackbar.notifyError(errorMessage);
      return;
    }

    let ticketAttributes = this.ticketTypeResource.ticketAttributes?.map(
      (attributeSummary: TicketAttributeSummary) => {
        return {
          descriptor_id: attributeSummary.descriptor.id,
          value: attributeSummary.serializedValue,
        };
      },
    );

    let ticketTypeId = this.args.newTicketTypeId;
    let statusId = this.effectiveSelectedStatusId;
    try {
      yield this.inboxApi.changeTicketType(this.args.conversation, {
        ticket_type_id: ticketTypeId,
        ticket_custom_state_id: statusId!,
        attributes: ticketAttributes ?? [],
      });
      this.snackbar.notify(
        this.intl.t('inbox.change-ticket-type-panel.ticket-type-changed', {
          ticket_type: this.ticketTypeResource.selectedTicketType?.name,
        }),
      );
      this.args.onSuccess?.();
      this.args.hidePanel();
    } catch (e: unknown) {
      if (e instanceof ResponseError && e.response?.status === 422) {
        let body = (yield e.response.json()) as { errors?: Array<{ message?: string }> };
        let message =
          body?.errors?.[0]?.message ??
          this.intl.t('inbox.change-ticket-type-panel.errors.change-failed');
        this.snackbar.notifyError(message);
      } else {
        this.snackbar.notifyError(
          this.intl.t('inbox.change-ticket-type-panel.errors.change-failed'),
        );
      }
    }
  }

  get isChangingTicketType() {
    return taskFor(this.changeTicketType).isRunning;
  }

  @action
  noopCompanySelection(_companyId: string | null, _isDefaultSelection: boolean) {
    // No-op: Company selection is disabled in read-only mode
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ChangeTicketTypePanel': typeof ChangeTicketTypePanel;
    'inbox2/change-ticket-type-panel': typeof ChangeTicketTypePanel;
  }
}
